import { createAsyncThunk, createSlice, Draft } from "@reduxjs/toolkit";
import { ApiProvider } from "@src/store/ApiProvider";
import { getInitialState } from "@src/store/helpers";
import { handleError } from "@src/store/getThunkAndSlice";

export const getPoListThunkAndSlice = <TItem>({
	name,
	path,
	fallbackPath,
}: {
	name: string;
	path: string;
	fallbackPath: string;
}) => {
	const initialState = getInitialState(new Array<TItem>());

	const thunk = createAsyncThunk<Array<TItem>, Record<string, never>>(name, async (_params, { rejectWithValue, signal }) => {
		try {
			return await ApiProvider.default.get<Array<TItem>>(path, {}, { signal });
		} catch (error) {
			if (signal.aborted) return rejectWithValue(handleError(error));
			try {
				return await ApiProvider.default.get<Array<TItem>>(fallbackPath, {}, { signal });
			} catch (fallbackError) {
				return rejectWithValue(handleError(fallbackError));
			}
		}
	});

	const slice = createSlice({
		name,
		initialState,
		reducers: {
			reset: (state) => {
				state.data = [];
			},
		},
		extraReducers: (builder) => {
			builder
				.addCase(thunk.pending, (state) => {
					state.loading = true;
					state.isInitialLoad = false;
				})
				.addCase(thunk.fulfilled, (state, action) => {
					state.loading = false;
					state.init = true;
					state.version++;
					state.error = null;
					state.data = action.payload as Array<Draft<TItem>>;
				})
				.addCase(thunk.rejected, (state, action) => {
					if (action.meta.aborted) return;
					state.loading = false;
					state.init = true;
					state.error = typeof action.payload === "string" ? action.payload : "Unknown error";
				});
		},
	});

	return { thunk, slice };
};
